import { Http } from "../http";

/**
 * 视频号上传认证信息
 *
 * 说明：本文件的两个接口调用逻辑从 actions/shipinhaoPublish/mock.ts 抽取而来，
 * 该流程已在图文发布链路中实测可用，视频上传复用同一套认证。
 */
export interface ShipinhaoUploadAuth {
    /** 微信号数字 uin，上传接口 X-Arguments 中的 weixinnum */
    uin: number;
    /** 上传接口 Authorization 头 */
    authKey: string;
    /** finderUsername，发布接口 _log_finder_id 使用 */
    finderUsername: string;
    /** 视频 filetype（服务端下发，实测 20302） */
    videoFileType: number;
    /** 图片/封面 filetype（服务端下发，实测 20304） */
    pictureFileType: number;
}

interface UserInfoResponse {
    errCode: number;
    errMsg: string;
    data?: {
        finderUser?: {
            uin: number;
            finderUsername: string;
            nickname?: string;
        };
    };
}

interface UploadAuthKeyResponse {
    errCode: number;
    errMsg: string;
    data?: {
        authKey: string;
        uin?: number;
        videoFileType?: number;
        pictureFileType?: number;
    };
}

/** 获取 13 位时间戳 */
function getTimeStamp(length: number): string {
    return Date.now().toString().substring(0, length);
}

/**
 * 获取用户信息（拿 finderUsername）
 */
async function getUserInfo(cookies: string, http: Http): Promise<UserInfoResponse> {
    return http.api<UserInfoResponse>({
        method: "post",
        url: "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/auth/auth_data",
        data: {
            timestamp: getTimeStamp(13),
            _log_finder_uin: "",
            _log_finder_id: "",
            rawKeyBuff: null,
            pluginSessionId: null,
            scene: 7,
            reqScene: 7,
        },
        headers: {
            cookie: cookies,
            referer: "https://channels.weixin.qq.com",
            "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
        },
        defaultErrorMsg: "获取用户信息失败",
    });
}

/**
 * 获取上传认证密钥（拿 authKey 与 uin）
 */
async function getUploadAuthKey(cookies: string, finderUsername: string, http: Http): Promise<UploadAuthKeyResponse> {
    return http.api<UploadAuthKeyResponse>({
        method: "post",
        url: "https://channels.weixin.qq.com/cgi-bin/mmfinderassistant-bin/helper/helper_upload_params",
        data: {
            timestamp: getTimeStamp(13),
            _log_finder_id: finderUsername,
            rawKeyBuff: null,
        },
        headers: {
            cookie: cookies,
            referer: "https://channels.weixin.qq.com",
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
            "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
        },
        defaultErrorMsg: "获取上传认证密钥失败",
    });
}

/** 认证失败时抛出，携带平台错误码便于上层归类 */
export class ShipinhaoAuthError extends Error {
    constructor(
        message: string,
        public readonly errCode: number,
    ) {
        super(message);
        this.name = "ShipinhaoAuthError";
    }
}

/**
 * 获取视频号上传所需的认证信息
 *
 * @param cookies cookie 字符串（`name=value; name=value` 形式）
 * @param http Http 实例（不走代理即可，认证接口对代理无要求）
 */
export async function getShipinhaoUploadAuth(cookies: string, http: Http): Promise<ShipinhaoUploadAuth> {
    const userInfo = await getUserInfo(cookies, http);

    if (userInfo.errCode !== 0 || !userInfo.data?.finderUser) {
        const isLoginExpired = userInfo.errCode === 300333 || userInfo.errCode === 300334;
        throw new ShipinhaoAuthError(
            isLoginExpired ? "登录失效" : userInfo.errMsg || "获取用户信息失败",
            userInfo.errCode || 500,
        );
    }

    const finderUsername = userInfo.data.finderUser.finderUsername;
    const authKeyResponse = await getUploadAuthKey(cookies, finderUsername, http);

    if (authKeyResponse.errCode !== 0 || !authKeyResponse.data?.authKey) {
        throw new ShipinhaoAuthError(`获取上传认证参数失败: ${authKeyResponse.errMsg}`, authKeyResponse.errCode || 500);
    }

    const uin = authKeyResponse.data.uin;
    if (!uin) {
        throw new ShipinhaoAuthError("获取用户 uin 失败", 500);
    }

    const videoFileType = authKeyResponse.data.videoFileType || 20302;
    const pictureFileType = authKeyResponse.data.pictureFileType || 20304;

    return { uin, authKey: authKeyResponse.data.authKey, finderUsername, videoFileType, pictureFileType };
}
